// src/modules/myvault/components/automations/ActionListEditor.tsx

// What? Orderable list of action cards with per-type editors.
// Why?  A rule runs actions in order; the UI surfaces that explicitly and
//       lets admins reorder, collapse, and remove entries without leaving
//       the page.
// How?  Simple up/down reorder buttons rather than full dnd -- keeps the
//       dependency surface tight. Each card renders the editor for the
//       currently-selected action type; switching type resets `config`
//       (we can't meaningfully preserve SEND_EMAIL fields across a switch
//       to WEBHOOK).

import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  IconButton,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type {
  ActionType,
  AutomationRuleAction,
} from "../../hooks/useAutomationRules";
import type { ActionEditorContext } from "./types";
import SendEmailAction from "./actions/SendEmailAction";
import CreateRecordAction from "./actions/CreateRecordAction";
import UpdateFieldAction from "./actions/UpdateFieldAction";
import RunSkillAction from "./actions/RunSkillAction";
import WebhookAction from "./actions/WebhookAction";
import InstantiateTemplateAction from "./actions/InstantiateTemplateAction";

const ACTION_LABELS: Record<ActionType, string> = {
  SEND_EMAIL: "Send email",
  CREATE_RECORD: "Create record",
  UPDATE_FIELD: "Update field",
  RUN_SKILL: "Run skill",
  WEBHOOK: "Webhook",
  INSTANTIATE_TEMPLATE: "Instantiate template",
};

const ACTION_ORDER: ActionType[] = [
  "SEND_EMAIL",
  "CREATE_RECORD",
  "UPDATE_FIELD",
  "RUN_SKILL",
  "WEBHOOK",
  "INSTANTIATE_TEMPLATE",
];

function defaultConfigForType(type: ActionType): Record<string, unknown> {
  switch (type) {
    case "SEND_EMAIL":
      return { recipients: [], cc: [], context: {} };
    case "CREATE_RECORD":
      return { data: {} };
    case "UPDATE_FIELD":
      return { value: "" };
    case "RUN_SKILL":
      return { args: {} };
    case "WEBHOOK":
      return { method: "POST", headers: {}, body: "" };
    case "INSTANTIATE_TEMPLATE":
      return { params: {} };
    default:
      return {};
  }
}

function renderEditor(
  type: ActionType,
  config: Record<string, unknown>,
  onChange: (patch: Record<string, unknown>) => void,
  context: ActionEditorContext,
): React.ReactNode {
  const props = { config, onChange, context };
  switch (type) {
    case "SEND_EMAIL":
      return <SendEmailAction {...props} />;
    case "CREATE_RECORD":
      return <CreateRecordAction {...props} />;
    case "UPDATE_FIELD":
      return <UpdateFieldAction {...props} />;
    case "RUN_SKILL":
      return <RunSkillAction {...props} />;
    case "WEBHOOK":
      return <WebhookAction {...props} />;
    case "INSTANTIATE_TEMPLATE":
      return <InstantiateTemplateAction {...props} />;
    default:
      return (
        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
          Unknown action type: {type}
        </Typography>
      );
  }
}

interface ActionListEditorProps {
  actions: AutomationRuleAction[];
  onChange: (next: AutomationRuleAction[]) => void;
  context: ActionEditorContext;
}

const ActionListEditor: React.FC<ActionListEditorProps> = ({
  actions,
  onChange,
  context,
}) => {
  const updateAction = (
    index: number,
    patch: Partial<AutomationRuleAction>,
  ) => {
    const next = actions.map((a, i) => (i === index ? { ...a, ...patch } : a));
    onChange(next);
  };

  const updateConfig = (index: number, patch: Record<string, unknown>) => {
    const next = actions.map((a, i) =>
      i === index ? { ...a, config: { ...a.config, ...patch } } : a,
    );
    onChange(next);
  };

  const changeType = (index: number, newType: ActionType) => {
    const current = actions[index];
    if (!current || current.type === newType) return;
    updateAction(index, {
      type: newType,
      config: defaultConfigForType(newType),
    });
  };

  const removeAction = (index: number) => {
    onChange(actions.filter((_, i) => i !== index));
  };

  const moveAction = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= actions.length) return;
    const next = [...actions];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    onChange(next);
  };

  const addAction = () => {
    onChange([
      ...actions,
      { type: "SEND_EMAIL", config: defaultConfigForType("SEND_EMAIL") },
    ]);
  };

  return (
    <Stack spacing={2}>
      {actions.length === 0 && (
        <Sheet
          variant="soft"
          sx={{ borderRadius: "sm", p: 3, textAlign: "center" }}
        >
          <Typography level="body-sm" sx={{ color: "text.tertiary", mb: 1 }}>
            No actions. The rule will evaluate but do nothing.
          </Typography>
        </Sheet>
      )}
      {actions.map((action, index) => (
        <Card key={index} variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography level="title-sm">Action {index + 1}</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => moveAction(index, -1)}
                    disabled={index === 0}
                    aria-label="Move up"
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => moveAction(index, 1)}
                    disabled={index === actions.length - 1}
                    aria-label="Move down"
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    onClick={() => removeAction(index)}
                    aria-label="Remove action"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              <FormControl>
                <FormLabel>Action type</FormLabel>
                <Select
                  size="sm"
                  value={action.type}
                  onChange={(_, val) => changeType(index, val as ActionType)}
                >
                  {ACTION_ORDER.map((t) => (
                    <Option key={t} value={t}>
                      {ACTION_LABELS[t]}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <Box>
                {renderEditor(
                  action.type,
                  action.config || {},
                  (patch) => updateConfig(index, patch),
                  context,
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}

      <Box>
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<AddIcon />}
          onClick={addAction}
        >
          Add action
        </Button>
      </Box>
    </Stack>
  );
};

export default ActionListEditor;
